import React from "react";
import { useAuth } from "../../context/AuthContext";
import Loader from "../utils/Loader";
import Toaster from "../utils/Toaster";
import { Grid, Box, Chip, Avatar } from "@mui/material";
import { useGetShipmentHistoryQuery } from "../../store/apiSlice";
import { useStoreInfo } from "../../context/StoreInfoContext";
import { AppTypography } from "../common/Typography";
import { Card } from "../common/Card";
import { OrderCardContentWrapper } from "../common/OrderCardContentWrapper";
import { Container } from "../common/Container";
import AppButton from "../common/Button";
import { useTheme } from "../../theme/ThemeContext";
import Pagination from "@mui/material/Pagination";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShoppingBagOutlined, ReceiptLongOutlined } from "@mui/icons-material";
import { usePreserveStoreIdNavigate } from "../utils/usePreserveStoreIdNavigate";

const MyOrders = () => {
  const { user } = useAuth();
  const navigate = usePreserveStoreIdNavigate();
  const userId = user?.id;
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page"), 10);
  const [page, setPage] = useState(pageParam > 0 ? pageParam : 1);

  const { customerId, storeId } = useStoreInfo();
  const { data, isLoading, isFetching, error } = useGetShipmentHistoryQuery(
    { customerId, userId, page, storeId },
    { skip: !userId || !customerId },
  );

  // Keep page in sync with URL
  React.useEffect(() => {
    const urlPage = parseInt(searchParams.get("page"), 10);
    if ((urlPage > 0 ? urlPage : 1) !== page) {
      setPage(urlPage > 0 ? urlPage : 1);
    }
  }, [searchParams]);

  const handlePageChange = (_, value) => {
    setPage(value);
    setSearchParams({ page: value });
  };

  if (isLoading || isFetching) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </Box>
    );
  }

  if (!userId) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ textAlign: "center", py: 8 }}>
          <AppTypography variant="h6">
            Please log in to view your orders.
          </AppTypography>
        </Box>
      </Container>
    );
  }

  if (error) {
    Toaster.show("Failed to load order history", "error");
    return (
      <Container
        maxWidth="sm"
        sx={{
          textAlign: "center",
          minHeight: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <AppTypography
            variant="h6"
            fontSize={18}
            sx={{ mb: 1, textAlign: "center", fontWeight: 500, color: "grey" }}
          >
            Failed to load order history.
          </AppTypography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 5,
            }}
          >
            <img src={theme?.empty_cart_image_url} alt="No Orders" />
          </Box>
        </Box>
      </Container>
    );
  }

  if (!data?.result?.data?.length) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: { xs: "60vh", md: "70vh" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: { xs: 4, md: 8 },
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Icon Container */}
          <Box
            sx={{
              width: { xs: 120, sm: 140, md: 160 },
              height: { xs: 120, sm: 140, md: 160 },
              borderRadius: "50%",
              backgroundColor: theme?.app_button_color
                ? `${theme.app_button_color}15`
                : "rgba(0, 0, 0, 0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              position: "relative",
            }}
          >
            {/* Main Icon */}
            <ReceiptLongOutlined
              sx={{
                fontSize: { xs: 60, sm: 70, md: 80 },
                color: theme?.app_button_color || theme?.app_text || "#666",
                opacity: 0.8,
              }}
            />
            {/* Decorative Icon */}
            <ShoppingBagOutlined
              sx={{
                fontSize: { xs: 30, sm: 35, md: 40 },
                color: theme?.app_button_color || theme?.app_text || "#666",
                opacity: 0.4,
                position: "absolute",
                bottom: { xs: 8, sm: 10, md: 12 },
                right: { xs: 8, sm: 10, md: 12 },
              }}
            />
          </Box>

          {/* Title */}
          <AppTypography
            variant="h4"
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              fontWeight: 700,
              color: theme?.app_text || theme?.text_color || "#333",
              mb: 1.5,
              textAlign: "center",
            }}
          >
            No Orders Yet
          </AppTypography>

          {/* Description */}
          <AppTypography
            variant="body1"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
              color: theme?.app_grey || "#666",
              textAlign: "center",
              maxWidth: "500px",
              mb: 4,
              lineHeight: 1.6,
              px: { xs: 2, sm: 0 },
            }}
          >
            You haven't placed any orders yet. Start shopping to see your order
            history here!
          </AppTypography>

          {/* Image (if available from theme) */}
          {theme?.empty_cart_image_url && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 4,
                maxWidth: { xs: "280px", sm: "320px", md: "400px" },
                width: "100%",
              }}
            >
              <img
                src={theme.empty_cart_image_url}
                alt="No Orders"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}

          {/* CTA Button */}
          <AppButton
            variant="contained"
            size="large"
            onClick={() => navigate("/")}
            startIcon={<ShoppingBagOutlined />}
            sx={{
              backgroundColor: theme?.app_button_color,
              color: theme?.app_button_text_color || "#fff",
              px: { xs: 4, sm: 5 },
              py: { xs: 1.25, sm: 1.5 },
              fontSize: { xs: "0.95rem", sm: "1rem" },
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: theme?.app_button_color
                ? `0 4px 12px ${theme.app_button_color}40`
                : "0 4px 12px rgba(0,0,0,0.15)",
              "&:hover": {
                backgroundColor:
                  theme?.app_hover_button_color || theme?.app_button_color,
                boxShadow: theme?.app_button_color
                  ? `0 6px 16px ${theme.app_button_color}50`
                  : "0 6px 16px rgba(0,0,0,0.2)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Start Shopping
          </AppButton>

          {/* Additional Help Text */}
          <AppTypography
            variant="body2"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              color: theme?.app_grey || "#999",
              textAlign: "center",
              mt: 3,
              opacity: 0.7,
            }}
          >
            Browse our collection and add items to your cart
          </AppTypography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 3 }}>
        <AppTypography
          variant="h2"
          sx={{
            color: "#101828",
            fontWeight: 400,
            fontSize: "24px",
          }}
        >
          My Orders
        </AppTypography>
        <AppTypography
          variant="body1"
          sx={{
            color: "#4A5565",
            fontWeight: 400,
            fontSize: "14px",
            mt: 0.5,
          }}
        >
          View and track your redemption history
        </AppTypography>
      </Box>
      <Grid container spacing={3}>
        {data?.result?.data?.map((order, idx) => {
          // Address summary
          const products = order.products;
          const addressSummary = [
            order.shipment.address1,
            order.shipment.address2,
            order.shipment.city,
            order.shipment.state,
            order.shipment.country,
            order.shipment.pincode,
          ]
            .filter(Boolean)
            .join(", ");

          return (
            <Grid item xs={12} key={order.shipment.id}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow:
                    "rgba(0, 0, 0, 0.12) 0px 1px 1px, rgba(0, 0, 0, 0.24) 0px 1px 1px",
                  background: theme.card_bg_color,
                  border: `1px solid ${theme.divider}`,
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  },
                  minHeight: "140px",
                }}
              >
                <OrderCardContentWrapper
                  order={order}
                  theme={theme}
                  addressSummary={addressSummary}
                  products={products}
                />
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {data?.result?.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <Pagination
            count={data.result.totalPages}
            page={page}
            onChange={handlePageChange}
            shape="circular"
            size="large"
            sx={{
              "& .MuiPaginationItem-root": {
                color: theme.app_button_color,
                fontWeight: 600,
              },
              "& .Mui-selected": {
                backgroundColor: theme.app_button_color,
                color: theme.app_button_text_color,
              },
              color: theme?.app_button_color || "primary",
            }}
          />
        </Box>
      )}
    </Container>
  );
};

export default MyOrders;
